import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CenterChart } from './OptionScope';
import './OptionScope.css';
import './CryptoTrading.css';

const API = '/api/crypto';

function money(value, max = 2) {
  const maximumFractionDigits = Math.max(0, Math.min(20, Number(max) || 0));
  return Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: Math.min(2, maximumFractionDigits),
    maximumFractionDigits
  });
}

export default function CryptoTrading() {
  const [board, setBoard] = useState(null);
  const [selected, setSelected] = useState('BTCUSDT');
  const [amount, setAmount] = useState(100);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(async (symbol = selected) => {
    try {
      const { data } = await axios.get(`${API}/live-board`, {
        params: { symbol, interval: '1m' },
        timeout: 25000
      });
      if (data?.ok === false) throw new Error(data.message || 'Crypto data unavailable');
      setBoard(data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Free public crypto feed unavailable');
    }
  }, [selected]);

  useEffect(() => {
    load(selected);
    const timer = setInterval(() => load(selected), 10000);
    return () => clearInterval(timer);
  }, [load, selected]);

  const choose = (symbol) => {
    setSelected(symbol);
    setNote('');
  };

  const buy = async () => {
    setBusy(true);
    setNote('');
    try {
      const { data } = await axios.post(`${API}/buy`, {
        symbol: selected,
        amountUsdt: Number(amount)
      }, { timeout: 20000 });
      setNote(`Paper BUY opened · ${data.position.coin} · ${money(data.position.amountUsdt)} USDT`);
      await load(selected);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Paper BUY failed');
    } finally {
      setBusy(false);
    }
  };

  const sell = async () => {
    setBusy(true);
    setNote('');
    try {
      const { data } = await axios.post(`${API}/sell`, {}, { timeout: 20000 });
      setNote(`Paper SELL complete · net ${data.trade.pnl >= 0 ? '+' : ''}${money(data.trade.pnl, 4)} USDT`);
      await load(selected);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Paper SELL failed');
    } finally {
      setBusy(false);
    }
  };

  const toggleOptionsBot = async () => {
    setBusy(true);
    setNote('');
    try {
      const nextEnabled = board?.optionsBot?.enabled === false;
      await axios.post(`${API}/options-bot`, { enabled: nextEnabled }, { timeout: 10000 });
      setNote(`Automated BTC hedged option-selling bot ${nextEnabled ? 'enabled' : 'paused'}`);
      await load(selected);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Options bot control failed');
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset free Crypto paper wallet and trade history?')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/reset`, {}, { timeout: 10000 });
      setNote('Paper wallet and automated options bot reset to 10,000 USDT');
      await load(selected);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Reset failed');
    } finally {
      setBusy(false);
    }
  };

  const wallet = board?.wallet || {};
  const signal = board?.signal || {};
  const position = board?.position;
  const optionsBot = board?.optionsBot || {};
  const optionPosition = optionsBot.position;
  const optionSettings = optionsBot.settings || {};
  const professionalSignal = optionsBot.lastSignal || {};
  const professionalFrames = professionalSignal.frames || {};
  const spot = Number(board?.chart?.spot) || 0;
  const support = Number(professionalSignal.keyLevels?.support || board?.chart?.support) || 0;
  const resistance = Number(professionalSignal.keyLevels?.resistance || board?.chart?.resistance) || 0;
  const supportHolding = spot > 0 && support > 0 && spot >= support;
  const resistanceReached = spot > 0 && resistance > 0 && spot >= resistance;
  const resistanceGap = Math.max(0, resistance - spot);
  const botNet = Number(optionsBot.dailyPnl) || 0;
  const frameRows = [professionalFrames.one, professionalFrames.five, professionalFrames.fifteen].filter(Boolean);
  const bullishFrames = frameRows.filter((frame) => frame.bullish).length;
  const bearishFrames = frameRows.filter((frame) => frame.bearish).length;
  const direction = professionalSignal.optionType === 'CALL' || bullishFrames >= 2
    ? 'up'
    : professionalSignal.optionType === 'PUT' || bearishFrames >= 2 ? 'down' : 'sideways';
  const directionLabel = direction === 'up' ? 'UP DIRECTION' : direction === 'down' ? 'DOWN DIRECTION' : 'SIDEWAYS / WAIT';
  const oneMinuteFrame = professionalFrames.one || {};
  const notifications = [
    {
      tone: direction,
      title: directionLabel,
      text: `${bullishFrames} bullish · ${bearishFrames} bearish across closed 1m, 5m and 15m candles`
    },
    {
      tone: supportHolding ? 'up' : 'down',
      title: supportHolding ? 'SUPPORT HOLDING' : 'SUPPORT BROKEN',
      text: `${board?.selectedSymbol || 'BTCUSDT'} ${money(Math.abs(spot - support))} USDT ${supportHolding ? 'above' : 'below'} ${money(support)} support`
    },
    {
      tone: resistanceReached ? 'up' : 'info',
      title: resistanceReached ? 'RESISTANCE REACHED' : 'RESISTANCE NOT BROKEN',
      text: resistanceReached
        ? `${board?.selectedSymbol || 'BTCUSDT'} is testing ${money(resistance)}`
        : `${money(resistanceGap)} USDT remains before ${money(resistance)} resistance`
    },
    {
      tone: Number(oneMinuteFrame.volumeRatio) >= 1.1 ? 'up' : 'warning',
      title: Number(oneMinuteFrame.volumeRatio) >= 1.1 ? 'VOLUME CONFIRMED' : 'LOW VOLUME',
      text: `Closed 1-minute volume is ${money(oneMinuteFrame.volumeRatio, 2)}× its recent average`
    },
    {
      tone: professionalSignal.optionType ? 'up' : 'warning',
      title: professionalSignal.optionType
        ? `${professionalSignal.optionType === 'CALL' ? 'BULL PUT' : 'BEAR CALL'} SELL QUALIFIED`
        : 'NO ENTRY YET',
      text: professionalSignal.reason || 'Collecting professional multi-timeframe evidence'
    },
    {
      tone: botNet > 0 ? 'up' : botNet < 0 ? 'down' : 'info',
      title: botNet > 0 ? 'BOT IN PROFIT' : botNet < 0 ? 'BOT IN LOSS' : 'BOT FLAT',
      text: `${botNet >= 0 ? '+' : ''}${money(botNet, 4)} USDT booked today · ${optionPosition ? 'position open' : 'no position open'}`
    }
  ];

  return (
    <div className="crypto-screen os-app os-kite">
      <div className="crypto-tickers">
        {(board?.tickers || []).map((coin) => (
          <button
            key={coin.symbol}
            type="button"
            className={`${selected === coin.symbol ? 'active' : ''} ${coin.changePct >= 0 ? 'up' : 'down'}`}
            onClick={() => choose(coin.symbol)}
          >
            <span>{coin.short}/USDT</span>
            <strong>{money(coin.price, coin.price < 1 ? 6 : 2)}</strong>
            <em>{coin.changePct >= 0 ? '+' : ''}{Number(coin.changePct || 0).toFixed(2)}%</em>
          </button>
        ))}
      </div>

      <header className="crypto-header">
        <div>
          <span className="crypto-live-dot">● LIVE</span>
          <h1>Crypto Trading · Free Paper Mode</h1>
          <p>24×7 hedged BTC option-selling paper bot · premium and theta strategy · no naked orders</p>
        </div>
        <div className="crypto-source">
          <strong>{board?.marketLabel || '24×7 CRYPTO MARKET'}</strong>
          <span>Source: {board?.source || 'connecting…'}</span>
        </div>
      </header>

      {error && <div className="crypto-message error">{error}</div>}
      {note && <div className="crypto-message success">{note}</div>}

      <section className="crypto-wallet">
        <div><span>Total equity</span><strong>{money(wallet.totalEquity, 4)} USDT</strong></div>
        <div><span>Free paper cash</span><strong>{money(wallet.freeCash, 4)} USDT</strong></div>
        <div><span>Booked net</span><strong className={wallet.realizedPnl >= 0 ? 'up' : 'down'}>{wallet.realizedPnl >= 0 ? '+' : ''}{money(wallet.realizedPnl, 4)}</strong></div>
        <div><span>Open net</span><strong className={wallet.unrealizedPnl >= 0 ? 'up' : 'down'}>{wallet.unrealizedPnl >= 0 ? '+' : ''}{money(wallet.unrealizedPnl, 4)}</strong></div>
        <div><span>Simulated fees</span><strong>{money(wallet.feesPaid, 4)} USDT</strong></div>
      </section>

      <main className="crypto-main">
        <div className="crypto-workspace">
          <div className="crypto-content">
        <section className={`crypto-signal ${signal.bias === 'BUY' ? 'buy' : signal.bias === 'SELL' ? 'sell' : 'wait'}`}>
          <div>
            <span>LIVE PAPER SIGNAL</span>
            <h2>{signal.action || 'ANALYSING'}</h2>
            <p>{(signal.reasons || []).join(' · ')}</p>
          </div>
          <div className="crypto-signal-stats">
            <strong>{Number(signal.confidence || 0)}%</strong>
            <span>EMA 9 {money(signal.ema9, 6)}</span>
            <span>EMA 21 {money(signal.ema21, 6)}</span>
            <span>RSI {money(signal.rsi14, 1)}</span>
          </div>
        </section>

        <section className="crypto-resistance-monitor">
          <div><span>{board?.selectedSymbol || 'BTCUSDT'} live</span><strong>${money(spot)}</strong></div>
          <div><span>Validated support</span><strong className={supportHolding ? 'up' : 'down'}>${money(support)}</strong></div>
          <div><span>Validated resistance</span><strong className={resistanceReached ? 'up' : ''}>${money(resistance)}</strong></div>
          <div><span>Level setup</span><strong>{String(professionalSignal.setup || 'WAIT').replaceAll('_', ' ')}</strong></div>
          <div>
            <span>Automated bot result</span>
            <strong className={botNet > 0 ? 'up' : botNet < 0 ? 'down' : ''}>
              {botNet > 0 ? 'PROFIT' : botNet < 0 ? 'LOSS' : 'FLAT'} · {botNet >= 0 ? '+' : ''}{money(botNet, 4)} USDT
            </strong>
          </div>
        </section>

        <section className={`crypto-options-bot ${optionPosition?.optionType?.toLowerCase() || 'scanning'}`}>
          <header>
            <div>
              <span>PROFESSIONAL V4 · SUPPORT/RESISTANCE · PAPER ONLY</span>
              <h2>BTC Hedged Option Selling Bot</h2>
              <p>{optionsBot.lastAction || 'Connecting to Deribit public options data…'}</p>
            </div>
            <div className="crypto-bot-control">
              <b className={optionsBot.enabled === false ? 'paused' : 'active'}>
                {optionsBot.enabled === false ? 'PAUSED' : String(optionsBot.status || 'SCANNING').replaceAll('_', ' ').toUpperCase()}
              </b>
              <button type="button" onClick={toggleOptionsBot} disabled={busy}>
                {optionsBot.enabled === false ? 'ENABLE BOT' : 'PAUSE NEW ENTRIES'}
              </button>
            </div>
          </header>

          <div className="crypto-bot-limits">
            <div><span>Professional score</span><strong>{professionalSignal.confidence || 0}/{optionSettings.minConfidence || 78}</strong></div>
            <div><span>Confirmation</span><strong>Closed 1m + 5m + 15m</strong></div>
            <div><span>Liquidity gate</span><strong>Spread ≤{optionSettings.maxSpreadPct || 20}%</strong></div>
            <div><span>Trade control</span><strong>{optionsBot.tradesToday || 0}/{optionSettings.maxTradesPerDay || 4} today</strong></div>
            <div><span>Daily net</span><strong className={Number(optionsBot.dailyPnl) >= 0 ? 'up' : 'down'}>{Number(optionsBot.dailyPnl) >= 0 ? '+' : ''}{money(optionsBot.dailyPnl, 4)} USDT</strong></div>
          </div>

          <div className="crypto-pro-evidence">
            {[
              ['1 minute', professionalFrames.one],
              ['5 minute', professionalFrames.five],
              ['15 minute', professionalFrames.fifteen]
            ].map(([label, frame]) => (
              <div key={label}>
                <span>{label}</span>
                <strong className={frame?.bullish ? 'up' : frame?.bearish ? 'down' : ''}>
                  {frame?.bullish ? 'BULLISH' : frame?.bearish ? 'BEARISH' : 'WAIT'}
                </strong>
                <small>RSI {money(frame?.rsi14, 1)} · Vol {money(frame?.volumeRatio, 2)}×</small>
              </div>
            ))}
            <div>
              <span>Entry decision</span>
              <strong>
                {professionalSignal.optionType === 'CALL'
                  ? 'SELL BULL PUT SPREAD'
                  : professionalSignal.optionType === 'PUT' ? 'SELL BEAR CALL SPREAD' : professionalSignal.candidate || 'WAIT'}
              </strong>
              <small>{professionalSignal.reason || 'Collecting closed candles'}</small>
            </div>
          </div>

          {optionPosition ? (
            <>
              <div className="crypto-option-position">
                <div><span>OPEN HEDGED SELL</span><strong>{String(optionPosition.strategy || '').replaceAll('_', ' ')}</strong></div>
                <div><span>SELL → BUY hedge</span><strong>{money(optionPosition.shortStrike)} → {money(optionPosition.hedgeStrike)}</strong></div>
                <div><span>Net credit / margin</span><strong>{money(optionPosition.netCredit, 4)} / {money(optionPosition.marginBlocked, 4)}</strong></div>
                <div><span>Net P&amp;L</span><strong className={Number(optionPosition.netPnl) >= 0 ? 'up' : 'down'}>{Number(optionPosition.netPnl) >= 0 ? '+' : ''}{money(optionPosition.netPnl, 4)} USDT</strong></div>
              </div>
              <div className="crypto-selling-risk">
                <div><span>Breakeven</span><strong>${money(optionPosition.breakeven)}</strong></div>
                <div><span>Protected level</span><strong>{String(optionPosition.levelSetup || '').replaceAll('_', ' ')} · {money(optionPosition.optionType === 'CALL' ? optionPosition.validatedSupport : optionPosition.validatedResistance)}</strong></div>
                <div><span>Max profit</span><strong className="up">+{money(optionPosition.maxProfitNet, 4)}</strong></div>
                <div><span>Max defined loss</span><strong className="down">−{money(optionPosition.maxLossNet, 4)}</strong></div>
                <div><span>Net theta</span><strong>{money(optionPosition.netTheta, 4)}</strong></div>
              </div>
            </>
          ) : (
            <p className="crypto-bot-wait">No spread open. Bot waits for closed 1m/5m/15m confirmation, then sells premium only with a farther-strike protective hedge. Naked selling is disabled.</p>
          )}
        </section>

        <CenterChart
          chart={board?.chart}
          intervalOptions={[1, 5, 15, 30]}
          initialIntervalMin={1}
          prefsStorageKey="cryptoChartPrefs"
          footerNote="Detailed order book main screen पर hidden है · free public feed और paper trading only"
        />

        <section className="crypto-order">
          <div>
            <span>Paper amount</span>
            <label>
              <input
                type="number"
                min="10"
                step="10"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <b>USDT</b>
            </label>
          </div>
          <button type="button" className="buy" onClick={buy} disabled={busy || !!position}>PAPER BUY</button>
          <button type="button" className="sell" onClick={sell} disabled={busy || !position}>PAPER SELL</button>
          <button type="button" className="reset" onClick={reset} disabled={busy}>RESET</button>
        </section>

        {position && (
          <section className="crypto-position">
            <div>
              <span>OPEN PAPER POSITION</span>
              <h3>{position.coin}/USDT · {Number(position.qty).toFixed(8)}</h3>
            </div>
            <div><span>Entry</span><strong>{money(position.entryPrice, 6)}</strong></div>
            <div><span>Live</span><strong>{money(position.currentPrice, 6)}</strong></div>
            <div><span>Net P&L</span><strong className={position.unrealizedPnl >= 0 ? 'up' : 'down'}>{position.unrealizedPnl >= 0 ? '+' : ''}{money(position.unrealizedPnl, 4)} USDT</strong></div>
          </section>
        )}

        <section className="crypto-history">
          <header>
            <h2>Paper trade history</h2>
            <span>Estimated fee {Number(board?.feeRatePct || 0.1).toFixed(2)}% per side</span>
          </header>
          {(board?.trades || []).length ? (
            <div className="crypto-trade-list">
              {board.trades.map((trade) => (
                <div key={trade.id}>
                  <strong className={trade.type === 'BUY' ? 'up' : 'down'}>{trade.type}</strong>
                  <span>{trade.symbol}</span>
                  <span>@ {money(trade.price, 6)}</span>
                  <span>{new Date(trade.at).toLocaleString('en-IN')}</span>
                  <b className={trade.pnl == null || trade.pnl >= 0 ? 'up' : 'down'}>
                    {trade.pnl == null ? 'OPENED' : `${trade.pnl >= 0 ? '+' : ''}${money(trade.pnl, 4)} USDT`}
                  </b>
                </div>
              ))}
            </div>
          ) : <p className="crypto-empty">No paper trades yet.</p>}
        </section>

        <section className="crypto-history">
          <header>
            <h2>Automated hedged option-selling history</h2>
            <span>Premium · theta · breakeven · defined max risk</span>
          </header>
          {(optionsBot.trades || []).length ? (
            <div className="crypto-trade-list option-list">
              {optionsBot.trades.map((trade) => (
                <div key={trade.id}>
                  <strong className={trade.type === 'SPREAD_OPEN' || trade.type === 'OPTION_BUY' ? 'up' : 'down'}>
                    {trade.type === 'SPREAD_OPEN' || trade.type === 'OPTION_BUY' ? 'OPEN' : 'EXIT'}
                  </strong>
                  <span>{String(trade.strategy || trade.optionType || '').replaceAll('_', ' ')}</span>
                  <span>{trade.shortInstrumentName ? `${trade.shortInstrumentName} / ${trade.hedgeInstrumentName}` : trade.instrumentName}</span>
                  <span>{new Date(trade.at).toLocaleString('en-IN')}</span>
                  <b className={trade.pnl == null || trade.pnl >= 0 ? 'up' : 'down'}>
                    {trade.pnl == null ? 'OPENED' : `${trade.pnl >= 0 ? '+' : ''}${money(trade.pnl, 4)} USDT`}
                  </b>
                </div>
              ))}
            </div>
          ) : <p className="crypto-empty">Bot is scanning; no automated option trade yet.</p>}
        </section>
          </div>

          <aside className={`crypto-live-intelligence ${direction}`}>
            <header>
              <span>LIVE CRYPTO NOTIFICATIONS</span>
              <h2>What is happening now?</h2>
              <small>Updates automatically every 10 seconds</small>
            </header>

            <div className={`crypto-direction-card ${direction}`}>
              <b>{direction === 'up' ? '↑' : direction === 'down' ? '↓' : '↔'}</b>
              <div>
                <span>Market direction</span>
                <strong>{directionLabel}</strong>
                <small>Professional score {professionalSignal.confidence || 0}/{optionSettings.minConfidence || 78}</small>
              </div>
            </div>

            <div className="crypto-notification-list">
              {notifications.map((item) => (
                <article key={item.title} className={item.tone}>
                  <i>{item.tone === 'up' ? '↑' : item.tone === 'down' ? '↓' : item.tone === 'warning' ? '!' : '•'}</i>
                  <div>
                    <strong>{item.title}</strong>
                    <p>{item.text}</p>
                  </div>
                </article>
              ))}
            </div>

            <footer>
              <span>Bot status</span>
              <strong>{String(optionsBot.status || 'connecting').replaceAll('_', ' ').toUpperCase()}</strong>
              <p>{optionsBot.lastAction || 'Connecting to production bot…'}</p>
              {optionsBot.lastTickAt && <small>Last analysis: {new Date(optionsBot.lastTickAt).toLocaleTimeString('en-IN')}</small>}
            </footer>
          </aside>
        </div>
      </main>

      <footer className="crypto-disclaimer">{board?.disclaimer || 'Paper trading only.'}</footer>
    </div>
  );
}
